import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../state/store';
import {
    fetchClientConfig,
    type ClientDomainConfig,
} from '../state/clientConfig/clientConfigSlice';
import { Logger } from '../utils/Logger';
import { apiClient } from '../utils/ApiClient';

const applyApiBaseUrl = (config?: ClientDomainConfig) => {
    if (config?.apiBaseUrl) {
        apiClient.setBaseUrl(config.apiBaseUrl);
        Logger.debug('useClientConfigInit', `Updated API base URL to: ${config.apiBaseUrl}`);
    }
};

export const useClientConfigInit = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { data, loading, loaded, error } = useSelector((state: RootState) => state.clientConfig);
    const fetchInitiated = useRef(false);

    const loadClientConfig = useCallback((hostname: string) => {
        Logger.debug('useClientConfigInit', `Fetching client config for domain: ${hostname}`);
        return dispatch(fetchClientConfig(hostname)).then((result: {
            meta: { requestStatus: string };
            payload?: ClientDomainConfig;
        }) => {
            if (result.meta.requestStatus === 'fulfilled') {
                applyApiBaseUrl(result.payload);
            }
            return result;
        });
    }, [dispatch]);

    useEffect(() => {
        if (fetchInitiated.current || loaded || loading) return;
        fetchInitiated.current = true;

        const hostname = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
        loadClientConfig(hostname);
    }, [loadClientConfig, loaded, loading]);

    const retry = useCallback(() => {
        const hostname = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
        return loadClientConfig(hostname);
    }, [loadClientConfig]);

    return { data, loading, loaded, error, retry };
};
